import axiosInstance from './axios/index';

export interface AbeStreamEvent {
  data: any;
  type:
    | 'text'
    | 'tool_call'
    | 'tool_result'
    | 'confirmation_required'
    | 'done'
    | 'error';
}

export interface AbeConfirmPayload {
  [key: string]: any;
  action: string;
  calendarEventId?: string;
  invoiceId?: string;
  message: string;
  taskId?: string;
  workerId?: string;
}

export interface AbeConfirmResult {
  data?: any;
  message: string;
  success: boolean;
}

export interface AbeSavedChartRecord {
  chartData: any;
  createdAt: string;
  dashboardId: string;
  id: string;
  originalSql: string;
  title: string;
  updatedAt: string;
}

export const streamAbeMessage = async (
  message: string,
  onEvent: (event: AbeStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> => {
  const token = localStorage.getItem('accessToken')?.replaceAll('"', '');
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:3000';

  const response = await fetch(`${baseUrl}/abe/chat`, {
    body: JSON.stringify({ message }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    method: 'POST',
    signal
  });

  if (!response.ok) {
    throw new Error(`Abe request failed: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  const processLines = (lines: string[]) => {
    lines.forEach((line, idx) => {
      if (line.startsWith('data: ')) {
        const eventLine = lines[idx - 1] ?? '';
        const eventType = eventLine.startsWith('event: ')
          ? (eventLine.replace('event: ', '').trim() as AbeStreamEvent['type'])
          : 'text';
        try {
          const data = JSON.parse(line.replace('data: ', ''));
          onEvent({ data, type: eventType });
        } catch {
          // malformed line — skip
        }
      }
    });
  };

  const pump = async (): Promise<void> => {
    const { done, value } = await reader.read();
    if (done) return;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    processLines(lines);

    pump();
  };

  await pump();
};

export const confirmAbeAction = async (
  action: string,
  payload: AbeConfirmPayload
): Promise<AbeConfirmResult> => {
  const response = await axiosInstance.post('/abe/confirm', {
    action,
    payload
  });
  return response.data;
};

export const clearAbeHistory = async (): Promise<void> => {
  await axiosInstance.delete('/abe/history');
};

export const getSavedCharts = async (
  dashboardId?: string
): Promise<AbeSavedChartRecord[]> => {
  const params = dashboardId ? { dashboardId } : {};
  const response = await axiosInstance.get('/abe/charts', { params });
  return response.data;
};

export const saveChartApi = async (
  title: string,
  chartData: any,
  originalSql?: string,
  dashboardId?: string
): Promise<AbeSavedChartRecord> => {
  const response = await axiosInstance.post('/abe/charts', {
    chartData,
    dashboardId,
    originalSql,
    title
  });
  return response.data;
};

export const deleteSavedChart = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/abe/charts/${id}`);
};

export const refreshSavedChart = async (
  id: string
): Promise<AbeSavedChartRecord> => {
  const response = await axiosInstance.post(`/abe/charts/${id}/refresh`);
  return response.data;
};

export default {
  clearAbeHistory,
  confirmAbeAction,
  deleteSavedChart,
  getSavedCharts,
  refreshSavedChart,
  saveChartApi,
  streamAbeMessage
};
